import { useCallback, useState } from "react";
import { INITIAL_CUSTOM_PRINTOUT_EFFORT } from "./consts";
import type { CustomPrintoutEffort, CustomPrintoutRepair } from "./customPrintoutTypes";

/**
 * What the design chat's composer holds: the words, the attached example, the thinking level, and — after Fix with
 * Annie — the failure the next request carries so the designer knows what to fix. Plus a key that moves focus into it.
 * Sending or clearing empties the box but keeps the thinking level: it stays as chosen until the popup closes.
 */
export const useDraftComposer = () => {
	const [prompt, setPrompt] = useState<string>("");
	const [effort, setEffort] = useState<CustomPrintoutEffort>(INITIAL_CUSTOM_PRINTOUT_EFFORT);
	const [file, setFile] = useState<File | null>(null);
	const [fixRepair, setFixRepair] = useState<CustomPrintoutRepair | null>(null);
	const [focusKey, setFocusKey] = useState<number>(0);

	const clear = useCallback(() => {
		setPrompt("");
		setFile(null);
		setFixRepair(null);
	}, []);

	/** Puts `text` in the box — with the failure it describes, for Fix with Annie — and focuses it. */
	const fill = useCallback((text: string, repair: CustomPrintoutRepair | null) => {
		setPrompt(text);
		setFixRepair(repair);
		setFocusKey((key) => key + 1);
	}, []);

	/** A failed request's words go back in the box to retry — unless the user already started typing something else. */
	const restore = useCallback((text: string) => setPrompt((current) => (current === "" ? text : current)), []);

	return { prompt, setPrompt, file, setFile, effort, setEffort, fixRepair, focusKey, clear, fill, restore };
};
